import React, { useState, useEffect, useCallback, useRef } from 'react';
import { S } from '../styles';
import { getTheme } from '../theme';
import { API, authHeaders, calendarDaysAgo } from '../utils/helpers';
import PickupWizard from './PickupWizard';
import CustomerPopover from './CustomerPopover';
import { PickupList, PickupCards, PickupTexts, SORTS, RANGES, rangeOf, groupTickets } from './PickupViews';

// Per-viewer conveniences, kept in this browser only.
const VIEW_KEY = 'spinboard-pickup-view';
const SORT_KEY = 'spinboard-pickup-sort';
const readPref = (k, fallback, allowed) => { try { const v = localStorage.getItem(k); return allowed.includes(v) ? v : fallback; } catch (_) { return fallback; } };
const writePref = (k, v) => { try { localStorage.setItem(k, v); } catch (_) {} };

function PickupPage({ theme = 'dark', currentUser = null }) {
  const T = getTheme(theme);
  const [tickets, setTickets] = useState([]);
  const [summary, setSummary] = useState(null);
  // Set when this list was built the slow way and is therefore not live. The
  // screen says how old it is and nothing more.
  //
  // It deliberately does NOT explain why. An earlier version described the
  // Lightspeed fault in full, which is the right level of detail for a log and
  // the wrong one for a person at the counter: it reads as the software
  // apologising for itself, and a tech who is told the plumbing is broken stops
  // trusting the screen even when the tickets on it are correct. What they need
  // is the timestamp, so they know whether to refresh before acting.
  const [stale, setStale] = useState(false);
  const [staleAt, setStaleAt] = useState(null);
  // No list yet at all — the first read of the whole table is still running. It
  // takes a few minutes and nothing waits on it, so the screen has to say what
  // is happening rather than show an empty queue as though that were the answer.
  const [building, setBuilding] = useState(false);
  const staleAgeText = () => {
    if (!staleAt) return 'just now';
    const mins = Math.round((Date.now() - staleAt) / 60000);
    if (mins < 1) return 'just now';
    return mins === 1 ? '1 minute ago' : mins + ' minutes ago';
  };
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [lastSync, setLastSync] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [contactSummary, setContactSummary] = useState({});
  const [pickupTrend, setPickupTrend] = useState(null);
  const [customerPopover, setCustomerPopover] = useState(null);
  const [collected, setCollected] = useState(undefined); // undefined = loading, null = unavailable
  const customerAnchorRefs = useRef({});

  // List or cards, and how they are sorted, remembered per person (7 Oct).
  const [view, setViewState] = useState(() => readPref(VIEW_KEY, 'list', ['list', 'cards']));
  const setView = (v) => { setViewState(v); writePref(VIEW_KEY, v); };
  const [sortKey, setSortState] = useState(() => readPref(SORT_KEY, 'oldest', SORTS.map(x => x.key)));
  const setSortKey = (v) => { setSortState(v); writePref(SORT_KEY, v); };
  const [range, setRange] = useState('all');

  // Texts in from the customers on this screen. Read every minute, and again
  // whenever the queue reloads, so a new "can I come at 5?" turns up on its own.
  const [texts, setTexts] = useState([]);
  const [textsError, setTextsError] = useState('');
  const loadTexts = useCallback(async (list) => {
    if (!list || !list.length) { setTexts([]); return; }
    try {
      const r = await fetch(`${API}/api/pickup/messages`, {
        method: 'POST', headers: authHeaders(true),
        body: JSON.stringify({ tickets: list.map(t => ({ id: t.id, phone: t.phone })) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not read recent texts.');
      setTexts(d.messages || []); setTextsError('');
    } catch (err) { setTextsError(err.message); }
  }, []);
  useEffect(() => {
    loadTexts(tickets);
    const id = setInterval(() => loadTexts(tickets), 60000);
    return () => clearInterval(id);
  }, [tickets, loadTexts]);

  // Wide screens get the texts as a column on the right; narrower ones above.
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1500);
  useEffect(() => {
    const on = () => setWide(window.innerWidth >= 1500);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);

  const loadContactSummary = useCallback(async (ticketList) => {
    if (!ticketList.length) return;
    try {
      const res = await fetch(`${API}/api/contact-log/summary`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ ticketIds: ticketList.map(t => t.id) })
      });
      const data = await res.json();
      setContactSummary(data);
    } catch (_) {}
  }, []);

  const handleLogUpdated = (ticketId, newLog) => {
    const logArray = Array.isArray(newLog) ? newLog : (newLog ? [newLog] : []);
    setContactSummary(prev => {
      if (logArray.length === 0) {
        const next = { ...prev };
        delete next[ticketId];
        return next;
      }
      const last = logArray[0];
      return {
        ...prev,
        [ticketId]: {
          count: logArray.length,
          lastAttempt: last,
          daysSince: calendarDaysAgo(last?.timestamp),
        }
      };
    });
  };

  useEffect(() => {
    const handler = (e) => {
      const { ticketId } = e.detail;
      if (!ticketId) return;
      setContactSummary(prev => {
        const key = String(ticketId);
        const existing = prev[key];
        return {
          ...prev,
          [key]: {
            count: (existing?.count || 0) + 1,
            lastAttempt: { method: 'email', outcome: 'sent', timestamp: new Date().toISOString() },
            daysSince: 0,
          }
        };
      });
    };
    window.addEventListener('spinboard-contact-updated', handler);
    return () => window.removeEventListener('spinboard-contact-updated', handler);
  }, []);

  useEffect(() => {
    if (!summary?.avgDaysOpen) return;
    const today = new Date().toISOString().slice(0, 10);
    const stored = JSON.parse(localStorage.getItem('spinboard-pickup-avg-history') || '[]');
    const filtered = stored.filter(e => e.date !== today);
    filtered.push({ date: today, avg: summary.avgDaysOpen });
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 90);
    const pruned = filtered.filter(e => new Date(e.date) >= cutoff);
    localStorage.setItem('spinboard-pickup-avg-history', JSON.stringify(pruned));
    if (pruned.length < 3) { setPickupTrend(null); return; }
    const hist = pruned.slice(0, -1);
    const histAvg = hist.reduce((sum, e) => sum + e.avg, 0) / hist.length;
    const diff = summary.avgDaysOpen - histAvg;
    setPickupTrend(diff > 0.5 ? 'up' : diff < -0.5 ? 'down' : 'neutral');
  }, [summary]);

  const [payments, setPayments] = useState({});

  // silent: refresh the data without dropping the view into its loading
  // state. Used when the view is already on screen and worth looking at —
  // rows update in place instead of the whole thing flashing empty.
  // fresh: ask the server to skip its 30-second response cache. Only ever set
  // by the Refresh button — an automatic or incidental reload has no reason to
  // spend the Lightspeed calls, but a person pressing Refresh does.
  const load = useCallback(async (silent = false, fresh = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${API}/api/pickup${fresh ? '?fresh=1' : ''}`, { credentials:'include', headers: authHeaders() });
      if (!res.ok) throw new Error('API error');
      const data = await res.json();
      setTickets(data.tickets);
      setSummary(data.summary);
      setLastSync(new Date());
      setStale(!!data.degraded); setStaleAt(data.asOf || null); setBuilding(!!data.building);

      // Payment badges — non-fatal, same as the service queue.
      const payIds = (data.tickets || []).map(t => t.id).filter(Boolean);
      if (payIds.length) {
        fetch(`${API}/api/payments/summary?workorderIds=${payIds.join(',')}`, {
          credentials: 'include',
          headers: { Authorization: `Bearer ${localStorage.getItem('sb_token')}` },
        })
          .then(r => r.ok ? r.json() : { summary: {} })
          .then(d => setPayments(d.summary || {}))
          .catch(() => {});
      }
      // Contact badges — one POST for the whole queue, not one GET per ticket.
      // Deliberately not awaited: badges fill in a beat later rather than
      // holding the entire table in its loading state. Also fixes a real bug —
      // the server computes daysSince in the shop's timezone, where the old
      // per-ticket loop did the arithmetic in the browser's.
      loadContactSummary(data.tickets || []);
    } catch (err) {
      console.error('Pickup fetch error:', err);
    }
    setLoading(false);
  }, [loadContactSummary]);

  useEffect(() => { load(); }, [load]);

  // Deliberately its own request rather than part of load(). The queue is the
  // slow screen and must never wait on this. The card shows a dash until it
  // lands, and a dash indefinitely if it fails — better than a confident
  // $0.00 on a money figure we could not actually read.
  //
  // Being separate is also how it got missed: Refresh called load(), which has
  // nothing to do with this request, so the card sat on its mount-time value
  // and only a browser reload moved it. Anything that refreshes the screen has
  // to call both.
  const loadCollected = useCallback(async (fresh = false) => {
    try {
      const r = await fetch(`${API}/api/pickup/collected-today${fresh ? '?fresh=1' : ''}`,
        { credentials:'include', headers: authHeaders() });
      const d = r.ok ? await r.json() : null;
      setCollected(d && !d.unavailable ? d : null);
    } catch (_) {
      setCollected(null);
    }
  }, []);

  useEffect(() => { loadCollected(); }, [loadCollected]);

  // Search and the Paid/Unpaid filter first; the letter range counts what is
  // left, so its chips say how many each would show.
  const searched = (() => {
    let list = filter === 'all' ? tickets : tickets.filter(t => filter === 'paid' ? t.isPaid : !t.isPaid);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        t.customer.toLowerCase().includes(q) ||
        t.bike.toLowerCase().includes(q) ||
        t.displayId.toLowerCase().includes(q) ||
        (t.phone || '').includes(q)
      );
    }
    return list;
  })();
  const rangeCounts = Object.fromEntries(RANGES.map(r => [r.key, searched.filter(t => rangeOf(t.customer) === r).length]));
  const inRange = range === 'all' ? searched : searched.filter(t => rangeOf(t.customer)?.key === range);
  const groups = groupTickets(inRange, sortKey);
  // Arrow keys in the wizard walk the bikes in the order they are shown.
  const filtered = groups.flatMap(g => g.items);
  const ticketsById = Object.fromEntries(tickets.map(t => [String(t.id), t]));
  const bikesPer = tickets.reduce((m, t) => { if (t.customerId && t.customerId !== '0') m[t.customerId] = (m[t.customerId] || 0) + 1; return m; }, {});
  // The newest text per bike, for the line under the customer's name.
  const lastText = texts.reduce((m, x) => { if (!m[x.ticketId]) m[x.ticketId] = x; return m; }, {});
  const openTicket = (t) => setSelectedTicket(selectedTicket?.id === t.id ? null : t);
  const toggleCustomer = (t) => setCustomerPopover(p => p?.customerId === t.customerId ? null : { customerId: t.customerId, customerName: t.customer });

  const selectedIndex = filtered.findIndex(t => t.id === selectedTicket?.id);
  const goNext = () => { if (selectedIndex < filtered.length - 1) setSelectedTicket(filtered[selectedIndex + 1]); };
  const goPrev = () => { if (selectedIndex > 0) setSelectedTicket(filtered[selectedIndex - 1]); };

  const fmtTime = (d) => d ? d.toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' }) : '';
  const PAGE_PAD = 14;
  const chip = (on) => ({ ...S.filterBtn(on), background: on ? T.filterActive : T.filterInactive, color: on ? T.filterActiveText : T.filterInactiveText, border: on ? 'none' : `1px solid ${T.filterBorder}` });
  const seg = (on) => ({ border:'none', borderRadius:6, padding:'6px 12px', fontSize:12.5, fontWeight:600, cursor:'pointer', fontFamily:'inherit',
    background: on ? T.card : 'transparent', color: on ? T.text : T.textSub, boxShadow: on ? '0 1px 3px rgba(0,0,0,0.12)' : 'none', display:'flex', alignItems:'center', gap:6 });
  const segWrap = { display:'flex', gap:2, padding:3, borderRadius:8, background: theme === 'dark' ? '#0f1724' : '#e4e4dc' };

  return (
    <div>
      {selectedTicket && <PickupWizard
        ticket={selectedTicket}
        onClose={() => {
          // A flagged ticket is usually opened to re-send the pickup text.
          // Reload quietly so the badge clears once that new quote is on
          // record, instead of lingering until the next manual refresh.
          const wasFlagged = !!selectedTicket?.balanceChanged;
          setSelectedTicket(null);
          if (wasFlagged) load(true);
          // A reply sent from the wizard marks the thread read.
          loadTexts(tickets);
        }}
        onNext={goNext}
        onPrev={goPrev}
        currentIndex={selectedIndex}
        totalCount={filtered.length}
        onLogUpdated={handleLogUpdated}
        onSendBack={() => load(true)}
        theme={theme}
        currentUser={currentUser}
      />}

      {customerPopover && (
        <CustomerPopover
          customerId={customerPopover.customerId}
          customerName={customerPopover.customerName}
          anchorRef={{ current: customerAnchorRefs.current[customerPopover.customerId] }}
          onClose={() => setCustomerPopover(null)}
          onViewHistory={(summary) => {
            sessionStorage.setItem('spinboard-customer-prefill', JSON.stringify({ id: summary.id, name: summary.name }));
            window.dispatchEvent(new CustomEvent('spinboard-nav', { detail: { tab: 'customers', customerId: summary.id, customerName: summary.name } }));
          }}
          theme={theme}
        />
      )}
      <div style={{ ...S.topbar, background:T.topbar }}>
        <div style={{ ...S.pageTitle, color:T.text }}>Ready for Pickup</div>
        <div style={S.syncInfo}>
          <div style={S.syncDot(true)} />
          {lastSync && <span style={{ color:T.textSub }}>Live · {fmtTime(lastSync)}</span>}
          <button style={{ ...S.btn(false), padding:'5px 14px', marginLeft:8, color:T.textSub, borderColor:T.filterBorder }} onClick={() => { load(false, true); loadCollected(true); }}>↻ Refresh</button>
        </div>
      </div>

      {(stale || building) && (
        <div style={{ fontSize:12, color:T.textSub, marginBottom:12 }}>
          {!staleAt ? 'Updating\u2026' : `Updated ${staleAgeText()}`}
        </div>
      )}

      {/* KPI strip */}
      {summary && (
        <div style={{ ...S.kpiGrid, gridTemplateColumns:'repeat(4, 1fr)', marginBottom:16 }}>
          <div style={{ ...S.kpi(), background:T.kpiCard, border:`1px solid ${T.cardBorder}` }}><div style={{ ...S.kpiLabel, color:T.textSub }}>Awaiting Pickup</div><div style={{ ...S.kpiValue(), color:T.kpiText }}>{summary.total}</div><div style={{ ...S.kpiSub, color:T.textSub }}>Finished bikes on floor</div></div>
          <div style={{ ...S.kpi('#e67e22'), background:T.kpiCard, border:`1px solid ${T.cardBorder}`, borderTop:'3px solid #e67e22' }}>
            <div style={{ ...S.kpiLabel, color:T.textSub }}>Queue Value</div>
            <div style={S.kpiValue('#e67e22')}>
              {summary.queueValue == null ? '—' : `$${Number(summary.queueValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            </div>
            <div style={{ ...S.kpiSub, color:T.textSub }}>
              Still to collect on {summary.unpaid} of {summary.total}
            </div>
          </div>
          <div style={{ ...S.kpi('#2ecc71'), background:T.kpiCard, border:`1px solid ${T.cardBorder}`, borderTop:'3px solid #2ecc71' }}>
            <div style={{ ...S.kpiLabel, color:T.textSub }}>Collected Today</div>
            <div style={S.kpiValue('#2ecc71')}>
              {collected == null ? '—' : `$${Number(collected.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            </div>
            <div style={{ ...S.kpiSub, color:T.textSub }}>
              {collected === undefined ? 'Checking the register…'
                : collected === null ? 'Could not read the register'
                : `${collected.count} ${collected.count === 1 ? 'pickup' : 'pickups'} rung out`}
            </div>
          </div>
          <div style={{ ...S.kpi(), background:T.kpiCard, border:`1px solid ${T.cardBorder}` }}>
            <div style={{ ...S.kpiLabel, color:T.textSub }}>Avg Days Open</div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ ...S.kpiValue(), color:T.kpiText }}>{summary.avgDaysOpen}</div>
              {pickupTrend === 'up' && <span style={{ color:'#e74c3c', fontSize:22, lineHeight:1, fontWeight:700 }}>↑</span>}
              {pickupTrend === 'down' && <span style={{ color:'#2ecc71', fontSize:22, lineHeight:1, fontWeight:700 }}>↓</span>}
            </div>
            <div style={{ ...S.kpiSub, color:T.textSub }}>
              {pickupTrend === 'up' ? 'Trending up vs 90d avg' : pickupTrend === 'down' ? 'Trending down vs 90d avg' : 'Days since service in'}
            </div>
          </div>
        </div>
      )}

      {/* Toolbar: sticks to the top on scroll so search, sort and the view
          switch stay in reach on a long queue. Real position:sticky (the window
          scrolls, nothing above clips overflow); the opaque background keeps
          cards from showing through as they pass under it. */}
      <div style={{ position:'sticky', top:0, zIndex:20, background:T.bg,
        marginLeft:-PAGE_PAD, marginRight:-PAGE_PAD, padding:`8px ${PAGE_PAD}px 6px` }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <div style={{ position:'relative', display:'flex', alignItems:'center' }}>
            <span style={{ position:'absolute', left:12, color:T.searchIcon, fontSize:15, pointerEvents:'none' }}>⌕</span>
            <input type="text" placeholder="Search customer, bike, ticket…" value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ background:T.card, border:`1px solid ${T.inputBorder}`, borderRadius:8,
                padding:'9px 30px 9px 34px', color:T.text, fontSize:13.5,
                fontFamily:"'Inter', sans-serif", width:280, outline:'none' }} />
            {search && <button onClick={() => setSearch('')} style={{ position:'absolute', right:9, background:'transparent', border:'none', color:T.textSub, cursor:'pointer', fontSize:16, lineHeight:1 }}>×</button>}
          </div>
          <div style={S.filterRow}>
            {[{k:'all',l:'All'},{k:'paid',l:'Pre-Paid'},{k:'unpaid',l:'Unpaid'}].map(f => (
              <button key={f.k} style={chip(filter===f.k)} onClick={() => setFilter(f.k)}>{f.l}</button>
            ))}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginLeft:'auto' }}>
            <span style={{ fontSize:11, fontWeight:700, color:T.tableHead, textTransform:'uppercase', letterSpacing:'0.08em' }}>Sort</span>
            <div style={segWrap} role="group" aria-label="Sort">
              {SORTS.map(o => <button key={o.key} style={seg(sortKey === o.key)} aria-pressed={sortKey === o.key} onClick={() => setSortKey(o.key)}>{o.label}</button>)}
            </div>
          </div>
          <div style={segWrap} role="group" aria-label="View">
            <button style={seg(view === 'list')} aria-pressed={view === 'list'} onClick={() => setView('list')} title="List view"><i className="ti ti-list" style={{ fontSize:15 }} />List</button>
            <button style={seg(view === 'cards')} aria-pressed={view === 'cards'} onClick={() => setView('cards')} title="Card view"><i className="ti ti-layout-grid" style={{ fontSize:15 }} />Cards</button>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:8, flexWrap:'wrap' }}>
          <span style={{ fontSize:11, fontWeight:700, color:T.tableHead, textTransform:'uppercase', letterSpacing:'0.08em', marginRight:2 }}>Last name</span>
          <button style={{ ...chip(range === 'all'), padding:'4px 11px', fontSize:12 }} onClick={() => setRange('all')}>All</button>
          {RANGES.map(r => (
            <button key={r.key} disabled={!rangeCounts[r.key]} onClick={() => setRange(range === r.key ? 'all' : r.key)}
              style={{ ...chip(range === r.key), padding:'4px 11px', fontSize:12, opacity: rangeCounts[r.key] ? 1 : 0.4, cursor: rangeCounts[r.key] ? 'pointer' : 'default' }}>
              {r.label} <span style={{ opacity:0.7, marginLeft:2 }}>{rangeCounts[r.key]}</span>
            </button>
          ))}
          <span style={{ ...S.sectionTitle, color:T.tableHead, whiteSpace:'nowrap', marginLeft:'auto' }}>
            {filtered.length} {filtered.length === 1 ? 'bike' : 'bikes'}{filtered.length !== tickets.length ? ` of ${tickets.length}` : ''}
          </span>
        </div>
      </div>

      <div style={{ display: wide ? 'grid' : 'block', gridTemplateColumns: wide ? 'minmax(0, 1fr) 330px' : undefined, gap:18, alignItems:'start', marginTop:4 }}>
        <div style={{ minWidth:0 }}>
          {!wide && !loading && <PickupTexts layout="strip" messages={texts} error={textsError} ticketsById={ticketsById} T={T} theme={theme} onOpen={openTicket} />}
          {loading ? <div style={{ ...S.emptyRow, color:T.emptyRow }}>Loading pickup queue…</div>
            : filtered.length === 0 ? (
              <div style={{ ...S.emptyRow, color:T.emptyRow }}>{tickets.length ? 'No bikes match.' : '🎉 No bikes waiting for pickup!'}</div>
            ) : view === 'cards' ? (
              <PickupCards groups={groups} T={T} theme={theme} selectedId={selectedTicket?.id} onOpen={openTicket} onCustomer={toggleCustomer}
                anchorRefs={customerAnchorRefs} contactSummary={contactSummary} payments={payments} lastText={lastText} bikesPer={bikesPer} />
            ) : (
              <PickupList groups={groups} T={T} theme={theme} selectedId={selectedTicket?.id} onOpen={openTicket} onCustomer={toggleCustomer}
                anchorRefs={customerAnchorRefs} contactSummary={contactSummary} payments={payments} lastText={lastText} bikesPer={bikesPer} />
            )}
          <div style={{ fontSize:11, color:T.textSub, margin:'14px 0 4px', textAlign:'right' }}>
            Click a bike to open its pickup · ← → to move between bikes · Esc to close · Click a name for the customer
          </div>
        </div>
        {wide && !loading && <PickupTexts layout="rail" messages={texts} error={textsError} ticketsById={ticketsById} T={T} theme={theme} onOpen={openTicket} />}
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────

export default PickupPage;
